'use client';

import { CustomSpin } from '@components/Common/CustomSpin';
import { setCookie } from '@utils/CookieUtil';
import { COOKIE_NAMES } from '@utils/constants';
import { getUTMs } from '@utils/utm';
import {
  LoginWithRedirectOptions,
  PromptOptions,
  useCommonAuthProvider,
} from 'app/_modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { useFlagsProxy } from 'app/_modules/feature-flags/hooks/useFlagsProxy';

import { RedirectLoginOptions } from '@auth0/auth0-spa-js';
import { Route } from 'next';
import { useRouter } from 'next/navigation';
import React, { FC, useEffect } from 'react';

function buildLoginParamsFromAuth0Options(
  options: RedirectLoginOptions,
  utms: Record<string, string>,
  cloudMspEnableUiFeatures: boolean,
): LoginWithRedirectOptions {
  const { authorizationParams, appState } = options;

  return {
    redirect_uri: authorizationParams?.redirect_uri,
    returnTo: appState?.returnTo,
    prompt: authorizationParams?.prompt as PromptOptions,
    connection: authorizationParams?.connection,
    organizationId: authorizationParams?.organizationId,
    customQueryParams: authorizationParams?.customQueryParams,
    utm: utms,
    ...(cloudMspEnableUiFeatures
      ? {
        screen_hint: authorizationParams?.screen_hint,
        loginHint: authorizationParams?.login_hint,
      }
      : {}
    ),
  };
}

export const Auth0: FC<RedirectLoginOptions> = (options) => {
  const router = useRouter();
  const { login, loginWithRedirect } = useCommonAuthProvider();
  const { serviceAuth0Available, cloudMspEnableUiFeatures, cloudAuthNewLoginExperience } = useFlagsProxy();

  useEffect(() => {
    if (serviceAuth0Available === undefined) {
      return;
    }

    if (!serviceAuth0Available) {
      router.push('/login-unavailable' as Route);

      return;
    }

    const params = new URLSearchParams(window?.location?.search);
    const criblDocs = params.get('criblDocs');

    if (criblDocs) {
      setCookie(COOKIE_NAMES.CRIBL_DOCS, criblDocs);
    }

    const utms = getUTMs();
    const loginParams = buildLoginParamsFromAuth0Options(options, utms, cloudMspEnableUiFeatures);

    if (cloudAuthNewLoginExperience && !options.authorizationParams?.organizationId) {
      void login(loginParams);

      return;
    }

    loginWithRedirect(loginParams);
  }, [
    serviceAuth0Available,
    options,
    cloudAuthNewLoginExperience,
    cloudMspEnableUiFeatures,
    login,
    loginWithRedirect,
    router,
  ]);

  return <CustomSpin />;
};
